import { Autocomplete, TextField, Stack, Box, FormHelperText } from '@mui/material';
import ItemSearchOption from './ItemSearchOption';
import {
  DndContext,
  useSensors,
  useSensor,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  closestCenter,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import SortableItem from './SortableItem';
import theme from '../../theme';
import SortableItemMO from './SortableItemMO';
import { useMediaQuery } from '@mui/material';

const ItemAddSection = ({
  albumOptions,
  isSearching,
  albumSearchText,
  setAlbumSearchText,
  handleAddAlbum,
  selectedAlbums,
  setSelectedAlbums,
  handleRemoveAlbum,
  error = false,
  helperText = '',
  placeholder = '앨범 또는 아티스트를 검색하세요.',
  type = 'ALBUM',
}) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setSelectedAlbums((items) => {
        const oldIndex = items.findIndex((item) => item.sourceId === active.id);
        const newIndex = items.findIndex((item) => item.sourceId === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  return (
    <Stack>
      <Autocomplete
        fullWidth
        options={albumOptions}
        value={null}
        getOptionLabel={(option) =>
          type === 'TRACK'
            ? `${option.artistName} - ${option.title}`
            : `${option.artist?.name || option.artistName} - ${option.title}`
        }
        isOptionEqualToValue={(option, value) =>
          option && value && option.sourceId === value.sourceId
        }
        loading={isSearching}
        loadingText="검색 중..."
        inputValue={albumSearchText}
        onInputChange={(_, newInputValue, reason) => {
          if (reason === 'reset') {
            setAlbumSearchText('');
          } else {
            setAlbumSearchText(newInputValue);
          }
        }}
        onChange={handleAddAlbum}
        onBlur={() => setAlbumSearchText('')}
        clearOnEscape={true}
        disableClearable={true}
        filterOptions={(x) => x}
        open={albumOptions.length > 0 || isSearching}
        noOptionsText=""
        renderOption={(props, option) => {
          return (
            <li {...props} key={`item-${option.sourceId}`}>
              <ItemSearchOption item={option} />
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            error={error}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '46px',
                fontSize: '14px',
                '& fieldset': {
                  borderColor: error ? theme.palette.red : theme.palette.grey.line,
                },
                '&:hover fieldset': {
                  borderColor: error ? theme.palette.red : theme.palette.black[9],
                },
                '&.Mui-focused fieldset': {
                  borderColor: error ? theme.palette.red : theme.palette.black[9],
                  borderWidth: '2px',
                },
              },
              '& .MuiInputBase-input::placeholder': {
                color: error ? theme.palette.black[9] : theme.palette.black[1],
                opacity: 1,
              },
            }}
          />
        )}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              '& .MuiAutocomplete-listbox': {
                padding: '8px 0',
                maxHeight: '400px',
              },
              '& .MuiAutocomplete-option': {
                padding: 0,
                paddingLeft: '0 !important',
                paddingRight: '0 !important',
              },
            },
          },
        }}
      />
      {helperText && (
        <FormHelperText
          error={error}
          sx={{
            textAlign: 'left',
            margin: '4px 0 0 0',
            fontSize: '12px',
            color: error ? theme.palette.red : theme.palette.black[1],
          }}
        >
          {helperText}
        </FormHelperText>
      )}

      {/* 드래그 앤 드롭 */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis, restrictToParentElement]}>
        <SortableContext
          items={selectedAlbums.map((a) => a.sourceId)}
          strategy={verticalListSortingStrategy}
        >
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {selectedAlbums.map((item, index) =>
              isMobile ? (
                <SortableItemMO
                  key={item.sourceId}
                  item={item}
                  index={index}
                  onRemove={handleRemoveAlbum}
                />
              ) : (
                <SortableItem
                  key={item.sourceId}
                  item={item}
                  index={index}
                  onRemove={handleRemoveAlbum}
                />
              ),
            )}
          </Box>
        </SortableContext>
      </DndContext>
    </Stack>
  );
};

export default ItemAddSection;
